import React, { useMemo, useRef, useState } from "react";
import Dropdown from "../../common/dropDown";
import {
  createEmptyColumn,
  ensureUniqueFieldName,
  getRepeatingColumnTypes,
  isChoiceWithOptions,
  isLayoutField,
  isNumericField,
  slugifyFieldName,
} from "./esourceFieldTypes";
import ESourceOptionEditor from "./ESourceOptionEditor";
import ESourceFormulaBuilder from "./ESourceFormulaBuilder";

const OPTIONS_LAYOUT_OPTIONS = [
  { value: "stacked", label: "One option per line" },
  { value: "inline", label: "All options on one line" },
];

/**
 * Field properties editor for the e-Source Form Builder.
 */
const ESourceFieldPropertiesForm = ({
  field,
  onChange,
  compact = false,
  allFields = [],
}) => {
  const choiceOptions = useMemo(() => {
    const fromField = Array.isArray(field?.options) ? field.options : [];
    return fromField.map((opt) => ({ value: String(opt), label: String(opt) }));
  }, [field?.options]);

  const defaultChoiceOptions = useMemo(
    () => [{ value: "", label: "None" }, ...choiceOptions],
    [choiceOptions],
  );

  const selectedDefaultChoice = useMemo(() => {
    const current =
      field?.defaultValue == null ? "" : String(field.defaultValue);
    return (
      defaultChoiceOptions.find((o) => o.value === current) ||
      defaultChoiceOptions[0]
    );
  }, [defaultChoiceOptions, field?.defaultValue]);

  const columnDragIndexRef = useRef(null);
  const [columnDragFromIndex, setColumnDragFromIndex] = useState(null);
  const [columnDragOverIndex, setColumnDragOverIndex] = useState(null);

  if (!field) return null;

  const idPrefix = `esource-field-${field.id}`;
  const layout = isLayoutField(field.type);
  const nonValueLayout = ["divider", "spacer", "pagebreak"].includes(
    field.type,
  );

  const selectedOptionsLayout =
    OPTIONS_LAYOUT_OPTIONS.find(
      (o) => o.value === (field.optionsLayout || "stacked"),
    ) || OPTIONS_LAYOUT_OPTIONS[0];

  const showPlaceholder =
    !layout &&
    field.type !== "checkbox" &&
    field.type !== "radio" &&
    field.type !== "yesno" &&
    field.type !== "toggle" &&
    field.type !== "button_group" &&
    field.type !== "signature" &&
    field.type !== "multi_select" &&
    field.type !== "repeating_table" &&
    field.type !== "calculated";

  const showRequired =
    !layout &&
    field.type !== "calculated" &&
    field.type !== "divider" &&
    field.type !== "spacer" &&
    field.type !== "pagebreak";

  const updateColumn = (colId, patch) => {
    const columns = (field.columns || []).map((c) => {
      if (c.id !== colId) return c;
      const next = { ...c, ...patch };
      if (patch.label && !patch.fieldName) {
        next.fieldName = ensureUniqueFieldName(
          patch.label,
          (field.columns || []).map((x) => ({
            id: x.id,
            fieldName: x.fieldName,
          })),
          colId,
        );
      }
      return next;
    });
    onChange(field.id, { columns });
  };

  const addColumn = () => {
    const columns = [...(field.columns || [])];
    columns.push(
      createEmptyColumn(
        { label: `Column ${columns.length + 1}`, type: "text" },
        columns,
      ),
    );
    onChange(field.id, { columns });
  };

  const removeColumn = (colId) => {
    onChange(field.id, {
      columns: (field.columns || []).filter((c) => c.id !== colId),
    });
  };

  const reorderColumns = (fromIndex, toIndex) => {
    const columns = [...(field.columns || [])];
    if (
      fromIndex == null ||
      toIndex == null ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= columns.length ||
      toIndex >= columns.length
    ) {
      return;
    }
    const [item] = columns.splice(fromIndex, 1);
    columns.splice(toIndex, 0, item);
    onChange(field.id, { columns });
  };

  const clearColumnDrag = () => {
    columnDragIndexRef.current = null;
    setColumnDragFromIndex(null);
    setColumnDragOverIndex(null);
  };

  const columnTypeOptions = getRepeatingColumnTypes();

  return (
    <div
      className={`esource-builder-props${
        compact ? " esource-builder-props--compact" : ""
      }`}
    >
      {!nonValueLayout ? (
        <div className="esource-builder-field esource-builder-field--span">
          <div className="esource-builder-props__label-head">
            <label
              className="esource-builder-field__label"
              htmlFor={`${idPrefix}-label`}
            >
              {field.type === "instruction" ? "Text" : "Label"}
            </label>
            {showRequired ? (
              <div className="esource-builder-props__required-inline">
                <label
                  className="esource-builder-field__label mb-0"
                  htmlFor={`${idPrefix}-required`}
                >
                  Required
                </label>
                <div className="form-check form-switch esource-builder-props__switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`${idPrefix}-required`}
                    checked={!!field.required}
                    onChange={(e) =>
                      onChange(field.id, { required: e.target.checked })
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
          <input
            id={`${idPrefix}-label`}
            className="form-control form-control-sm"
            value={field.label || ""}
            onChange={(e) => {
              const label = e.target.value;
              const patch = { label };
              if (
                !field.fieldName ||
                field.fieldName === slugifyFieldName(field.label)
              ) {
                patch.fieldName = ensureUniqueFieldName(
                  label || "field",
                  allFields,
                  field.id,
                );
              }
              onChange(field.id, patch);
            }}
          />
        </div>
      ) : null}

      {showPlaceholder ? (
        <div className="esource-builder-field">
          <label
            className="esource-builder-field__label"
            htmlFor={`${idPrefix}-placeholder`}
          >
            Placeholder
          </label>
          <input
            id={`${idPrefix}-placeholder`}
            className="form-control form-control-sm"
            value={field.placeholder || ""}
            onChange={(e) =>
              onChange(field.id, { placeholder: e.target.value })
            }
            placeholder="e.g. Enter details here"
          />
        </div>
      ) : null}

      {!layout && field.type !== "calculated" ? (
        <div className="esource-builder-field">
          <label
            className="esource-builder-field__label"
            htmlFor={`${idPrefix}-help`}
          >
            Help text
          </label>
          <input
            id={`${idPrefix}-help`}
            className="form-control form-control-sm"
            value={field.helpText || ""}
            onChange={(e) => onChange(field.id, { helpText: e.target.value })}
            placeholder="Shown under the field"
          />
        </div>
      ) : null}

      {!layout &&
      field.type !== "checkbox" &&
      field.type !== "multi_select" &&
      field.type !== "repeating_table" &&
      field.type !== "calculated" &&
      !isChoiceWithOptions(field.type) ? (
        <div className="esource-builder-field">
          <label
            className="esource-builder-field__label"
            htmlFor={`${idPrefix}-default`}
          >
            Default value
          </label>
          <input
            id={`${idPrefix}-default`}
            className="form-control form-control-sm"
            value={
              field.defaultValue == null ? "" : String(field.defaultValue)
            }
            onChange={(e) =>
              onChange(field.id, { defaultValue: e.target.value })
            }
          />
        </div>
      ) : null}

      {layout && field.type === "section" ? (
        <div className="esource-builder-field">
          <label
            className="esource-builder-field__label"
            htmlFor={`${idPrefix}-section-hint`}
          >
            Section hint
          </label>
          <input
            id={`${idPrefix}-section-hint`}
            className="form-control form-control-sm"
            value={field.helpText || ""}
            onChange={(e) => onChange(field.id, { helpText: e.target.value })}
          />
        </div>
      ) : null}

      {isChoiceWithOptions(field.type) ? (
        <>
          {field.type === "radio" || field.type === "yesno" ? (
            <div className="esource-builder-field">
              <label className="esource-builder-field__label">
                Options layout
              </label>
              <Dropdown
                options={OPTIONS_LAYOUT_OPTIONS}
                isMulti={false}
                showSearch={false}
                selected={selectedOptionsLayout}
                onSelect={(opt) =>
                  onChange(field.id, {
                    optionsLayout: opt?.value || "stacked",
                  })
                }
              />
            </div>
          ) : null}
          <div className="esource-builder-field esource-builder-field--span">
            <label className="esource-builder-field__label">Options</label>
            {field.type === "yesno" ? (
              <>
                <input
                  className="form-control form-control-sm"
                  value="Yes / No"
                  disabled
                />
                <span className="form-text">Yes / No options are fixed.</span>
              </>
            ) : field.type === "toggle" ? (
              <ESourceOptionEditor
                idPrefix={`${idPrefix}-opt`}
                options={
                  Array.isArray(field.options) && field.options.length >= 2
                    ? field.options.slice(0, 2)
                    : ["Yes", "No"]
                }
                minItems={2}
                maxItems={2}
                hint="First = On label, second = Off label."
                onChange={(next) => {
                  const cleaned = [...next];
                  if (cleaned.length < 1) cleaned.push("Yes");
                  if (cleaned.length < 2) cleaned.push("No");
                  const opts = cleaned.slice(0, 2);
                  const patch = { options: opts };
                  const currentDefault =
                    field.defaultValue == null
                      ? ""
                      : String(field.defaultValue);
                  if (currentDefault && !opts.includes(currentDefault)) {
                    patch.defaultValue = opts[1] || "No";
                  }
                  onChange(field.id, patch);
                }}
              />
            ) : (
              <ESourceOptionEditor
                idPrefix={`${idPrefix}-opt`}
                options={field.options || []}
                minItems={2}
                hint="Add, edit, reorder, or remove options."
                onChange={(next) => {
                  const patch = { options: next };
                  const currentDefault =
                    field.defaultValue == null
                      ? ""
                      : String(field.defaultValue);
                  if (currentDefault && !next.includes(currentDefault)) {
                    patch.defaultValue = "";
                  }
                  onChange(field.id, patch);
                }}
              />
            )}
          </div>
          {field.type !== "multi_select" ? (
            <div className="esource-builder-field">
              <label className="esource-builder-field__label">
                Default value
              </label>
              <Dropdown
                options={defaultChoiceOptions}
                isMulti={false}
                showSearch={false}
                selected={selectedDefaultChoice}
                onSelect={(opt) =>
                  onChange(field.id, {
                    defaultValue: opt?.value || "",
                  })
                }
              />
            </div>
          ) : null}
        </>
      ) : null}

      {field.type === "text" || field.type === "textarea" ? (
        <>
          <div className="esource-builder-props__pair">
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-min-length`}
              >
                Min characters
              </label>
              <input
                id={`${idPrefix}-min-length`}
                className="form-control form-control-sm"
                type="number"
                min="0"
                value={field.minLength || ""}
                placeholder="No min"
                onChange={(e) =>
                  onChange(field.id, { minLength: e.target.value })
                }
              />
            </div>
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-max-length`}
              >
                Max characters
              </label>
              <input
                id={`${idPrefix}-max-length`}
                className="form-control form-control-sm"
                type="number"
                min="1"
                value={field.maxLength || ""}
                placeholder="No limit"
                onChange={(e) =>
                  onChange(field.id, { maxLength: e.target.value })
                }
              />
            </div>
          </div>
          <div className="esource-builder-field esource-builder-field--span">
            <label
              className="esource-builder-field__label"
              htmlFor={`${idPrefix}-pattern`}
            >
              Pattern (regex)
            </label>
            <input
              id={`${idPrefix}-pattern`}
              className="form-control form-control-sm"
              value={field.pattern || ""}
              placeholder="e.g. ^[A-Z0-9]+$"
              onChange={(e) => onChange(field.id, { pattern: e.target.value })}
            />
          </div>
        </>
      ) : null}

      {field.type === "integer" || field.type === "number" ? (
        <>
          <div className="esource-builder-props__pair">
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-min`}
              >
                Min
              </label>
              <input
                id={`${idPrefix}-min`}
                className="form-control form-control-sm"
                type="number"
                value={field.minValue || ""}
                onChange={(e) =>
                  onChange(field.id, { minValue: e.target.value })
                }
              />
            </div>
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-max`}
              >
                Max
              </label>
              <input
                id={`${idPrefix}-max`}
                className="form-control form-control-sm"
                type="number"
                value={field.maxValue || ""}
                onChange={(e) =>
                  onChange(field.id, { maxValue: e.target.value })
                }
              />
            </div>
          </div>
          {field.type === "integer" ? (
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-step`}
              >
                Step
              </label>
              <input
                id={`${idPrefix}-step`}
                className="form-control form-control-sm"
                type="number"
                min="1"
                step="1"
                value={field.step || ""}
                placeholder="1"
                onChange={(e) => onChange(field.id, { step: e.target.value })}
              />
            </div>
          ) : (
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-decimals`}
              >
                Decimal places
              </label>
              <input
                id={`${idPrefix}-decimals`}
                className="form-control form-control-sm"
                type="number"
                min="0"
                max="10"
                value={field.decimalPlaces || ""}
                placeholder="Any"
                onChange={(e) =>
                  onChange(field.id, { decimalPlaces: e.target.value })
                }
              />
            </div>
          )}
          <div className="esource-builder-field">
            <label
              className="esource-builder-field__label"
              htmlFor={`${idPrefix}-unit`}
            >
              Unit
            </label>
            <input
              id={`${idPrefix}-unit`}
              className="form-control form-control-sm"
              value={field.unit || ""}
              placeholder="e.g. kg, mmHg"
              onChange={(e) => onChange(field.id, { unit: e.target.value })}
            />
          </div>
        </>
      ) : null}

      {field.type === "date" || field.type === "datetime" ? (
        <>
          <div className="esource-builder-props__pair">
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-min-date`}
              >
                Min date
              </label>
              <input
                id={`${idPrefix}-min-date`}
                className="form-control form-control-sm"
                type="date"
                value={field.minDate || ""}
                onChange={(e) =>
                  onChange(field.id, { minDate: e.target.value })
                }
              />
            </div>
            <div className="esource-builder-field">
              <label
                className="esource-builder-field__label"
                htmlFor={`${idPrefix}-max-date`}
              >
                Max date
              </label>
              <input
                id={`${idPrefix}-max-date`}
                className="form-control form-control-sm"
                type="date"
                value={field.maxDate || ""}
                onChange={(e) =>
                  onChange(field.id, { maxDate: e.target.value })
                }
              />
            </div>
          </div>
          <div className="esource-builder-field esource-builder-field--span esource-builder-props__checks">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${idPrefix}-no-future`}
                checked={!!field.dontAllowFuture}
                onChange={(e) =>
                  onChange(field.id, { dontAllowFuture: e.target.checked })
                }
              />
              <label
                className="form-check-label"
                htmlFor={`${idPrefix}-no-future`}
              >
                Do not allow future dates
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${idPrefix}-no-past`}
                checked={!!field.dontAllowPast}
                onChange={(e) =>
                  onChange(field.id, { dontAllowPast: e.target.checked })
                }
              />
              <label
                className="form-check-label"
                htmlFor={`${idPrefix}-no-past`}
              >
                Do not allow past dates
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${idPrefix}-default-today`}
                checked={!!field.defaultCurrentDate}
                onChange={(e) =>
                  onChange(field.id, { defaultCurrentDate: e.target.checked })
                }
              />
              <label
                className="form-check-label"
                htmlFor={`${idPrefix}-default-today`}
              >
                Default to current date/time
              </label>
            </div>
          </div>
        </>
      ) : null}

      {field.type === "repeating_table" ? (
        <div className="esource-builder-field esource-builder-field--span">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="esource-builder-field__label mb-0">Columns</label>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={addColumn}
            >
              Add column
            </button>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="esource-builder-field__label">Min rows</label>
              <input
                className="form-control form-control-sm"
                type="number"
                min="0"
                value={field.minRows ?? ""}
                onChange={(e) =>
                  onChange(field.id, { minRows: e.target.value })
                }
              />
            </div>
            <div className="col-6">
              <label className="esource-builder-field__label">Max rows</label>
              <input
                className="form-control form-control-sm"
                type="number"
                min="1"
                value={field.maxRows ?? ""}
                onChange={(e) =>
                  onChange(field.id, { maxRows: e.target.value })
                }
              />
            </div>
          </div>
          <div className="esource-builder-field">
            <label className="esource-builder-field__label">Add row label</label>
            <input
              className="form-control form-control-sm"
              value={field.addRowLabel || ""}
              onChange={(e) =>
                onChange(field.id, { addRowLabel: e.target.value })
              }
            />
          </div>
          <ul className="list-unstyled esource-rt-cols mb-0">
            {(field.columns || []).map((col, index) => {
              const typeSel =
                columnTypeOptions.find((t) => t.value === col.type) ||
                columnTypeOptions[0];
              return (
                <li
                  key={col.id}
                  className={`esource-rt-cols__item${
                    columnDragOverIndex === index ? " is-drag-over" : ""
                  }${
                    columnDragFromIndex === index ? " is-dragging" : ""
                  }`}
                  onDragOver={(e) => {
                    if (columnDragIndexRef.current == null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (columnDragOverIndex !== index) {
                      setColumnDragOverIndex(index);
                    }
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setColumnDragOverIndex((cur) =>
                      cur === index ? null : cur,
                    );
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = columnDragIndexRef.current;
                    clearColumnDrag();
                    reorderColumns(from, index);
                  }}
                >
                  <span
                    className="esource-rt-cols__grip"
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                    draggable
                    onDragStart={(e) => {
                      columnDragIndexRef.current = index;
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", col.id);
                      setColumnDragFromIndex(index);
                      setColumnDragOverIndex(index);
                    }}
                    onDragEnd={clearColumnDrag}
                  >
                    <i className="fas fa-grip-vertical" aria-hidden="true" />
                  </span>
                  <input
                    className="form-control form-control-sm"
                    value={col.label || ""}
                    placeholder="Column label"
                    onChange={(e) =>
                      updateColumn(col.id, { label: e.target.value })
                    }
                  />
                  <Dropdown
                    options={columnTypeOptions}
                    isMulti={false}
                    showSearch={false}
                    selected={typeSel}
                    onSelect={(opt) =>
                      updateColumn(col.id, {
                        type: opt?.value || "text",
                        options:
                          opt?.value === "dropdown"
                            ? col.options?.length
                              ? col.options
                              : ["Option 1", "Option 2"]
                            : [],
                      })
                    }
                  />
                  <div className="esource-rt-cols__actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="Delete"
                      onClick={() => removeColumn(col.id)}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  </div>
                  {col.type === "dropdown" ? (
                    <div className="esource-rt-cols__opts">
                      <ESourceOptionEditor
                        idPrefix={`${idPrefix}-col-${col.id}`}
                        options={col.options || []}
                        minItems={2}
                        hint="Dropdown options for this column. Drag to reorder."
                        onChange={(next) =>
                          updateColumn(col.id, { options: next })
                        }
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {field.type === "calculated" ? (
        <div className="esource-builder-field esource-builder-field--span">
          <label className="esource-builder-field__label">Formula</label>
          <ESourceFormulaBuilder
            formula={field.formula}
            fields={(allFields || []).filter(
              (f) => f.id !== field.id && isNumericField(f.type),
            )}
            onChange={(formula) => onChange(field.id, { formula })}
          />
        </div>
      ) : null}

      {!compact ? (
        <small className="text-muted">ID: {field.id}</small>
      ) : null}
    </div>
  );
};

export default ESourceFieldPropertiesForm;
